import React from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchRevenueReport,
  fetchSubscriptionDunning,
  fetchSubscriptionConflicts,
  setRevenueEventExcluded,
  type RevenueEntry,
  type RevenueMonth,
  type RevenueReport,
} from "../../api/admin";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { StatBlock } from "../../components/StatBlock";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { Segmented } from "./usage-ui";
import { formatDay, formatMonth, formatPence, sourceLabel } from "./subscription-ui";

// Revenue tab (spec 36). Everything here is computed live from one ledger of
// sales and refunds (packages/db/src/revenue.ts), so every tile is a sum of
// the rows in "Every sale". Test purchases, admin accounts and rows marked
// "not real money" are listed but never counted; gifted Pro is never revenue.
// Charts are inline SVG (no chart library, per DECISIONS).

const TYPE_COLOR = {
  subscription: "var(--sky-500)",
  full_history: "var(--spruce-500)",
  store: "var(--text-secondary)",
} as const;

const TYPE_LABEL = {
  subscription: "Subscriptions",
  full_history: "Full history",
  store: "Store",
} as const;

const GIFT_LABEL: Record<string, string> = {
  admin: "Complimentary",
  invite: "Partner or beta invite",
  key: "Plan key",
  admin_account: "Admin accounts",
  none: "No source recorded",
};

function money(n: number): React.ReactNode {
  return <span className="tnum" style={{ fontFamily: "var(--font-mono)" }}>{formatPence(n)}</span>;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

// ─── Revenue by month (stacked by type) ─────────────────────────────────────

function MonthBars({ months }: { months: RevenueMonth[] }) {
  const width = 640;
  const height = 160;
  const pad = { l: 8, r: 8, t: 8, b: 22 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const parts = (m: RevenueMonth) =>
    [
      { type: "subscription" as const, value: m.subscriptionsPence },
      { type: "full_history" as const, value: m.fullHistoryPence },
      { type: "store" as const, value: m.storePence },
    ].filter((p) => p.value > 0);
  const max = Math.max(1, ...months.map((m) => m.subscriptionsPence + m.fullHistoryPence + m.storePence));
  const slot = innerW / Math.max(1, months.length);
  const barW = Math.max(6, Math.min(40, slot * 0.6));

  if (months.every((m) => parts(m).length === 0)) {
    return <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No sales in this window yet.</p>;
  }

  return (
    <div className="r-scroll-x" style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ minWidth: 420, display: "block" }} role="img" aria-label="Sales by month">
        {months.map((m, i) => {
          const x = pad.l + i * slot + (slot - barW) / 2;
          let y = pad.t + innerH;
          return (
            <g key={m.month}>
              {parts(m).map((p) => {
                const h = (p.value / max) * innerH;
                y -= h;
                return (
                  <rect key={p.type} x={x} y={y} width={barW} height={h} rx={2} fill={TYPE_COLOR[p.type]}>
                    <title>{`${formatMonth(m.month)} · ${TYPE_LABEL[p.type]} · ${formatPence(p.value)}`}</title>
                  </rect>
                );
              })}
              <text x={x + barW / 2} y={height - 6} textAnchor="middle" fontSize={10} fill="var(--text-muted)" fontFamily="var(--font-mono)">
                {formatMonth(m.month)}
              </text>
            </g>
          );
        })}
      </svg>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
        {(Object.keys(TYPE_LABEL) as (keyof typeof TYPE_LABEL)[]).map((t) => (
          <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: TYPE_COLOR[t] }} /> {TYPE_LABEL[t]}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Every sale ──────────────────────────────────────────────────────────────

function describe(e: RevenueEntry): string {
  const plan = e.product === "yearly" ? "yearly" : e.product === "monthly" ? "monthly" : null;
  switch (e.kind) {
    case "new_subscription":
      return plan ? `New subscription, ${plan}` : "New subscription";
    case "renewal":
      return plan ? `Renewal, ${plan}` : "Renewal";
    case "full_history":
      return "Full history";
    case "store":
      return "Store purchase";
    case "refund":
      return e.type === "full_history" ? "Refund, Full history" : e.type === "store" ? "Refund, store" : "Refund, subscription";
  }
}

function StatusCell({ e }: { e: RevenueEntry }) {
  if (e.counted) return <span style={{ color: "var(--text-secondary)" }}>Counted</span>;
  if (e.notCountedReason === "test_purchase") return <Badge>Test purchase</Badge>;
  if (e.notCountedReason === "admin_account") return <Badge>Admin account</Badge>;
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <Badge>Not real money</Badge>
      {e.note && <span style={{ color: "var(--text-muted)" }}>{e.note}</span>}
    </span>
  );
}

function SalesTable({ entries, th, td }: { entries: RevenueEntry[]; th: React.CSSProperties; td: React.CSSProperties }) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: (e: RevenueEntry) => setRevenueEventExcluded(e.id, { excluded: e.counted }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin/subscriptions/revenue"] }),
  });

  if (entries.length === 0) {
    return <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No sales yet.</p>;
  }
  return (
    <div className="r-scroll-x" style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: 720 }}>
        <thead>
          <tr>
            <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Date</th>
            <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Who</th>
            <th className="eyebrow" style={{ ...th, textAlign: "left" }}>What</th>
            <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Via</th>
            <th className="eyebrow" style={th}>Amount</th>
            <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Status</th>
            <th className="eyebrow" style={th} />
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const dim = !e.counted;
            const canToggle = e.counted || e.marked;
            return (
              <tr key={e.id} style={{ borderTop: "1px solid var(--border-subtle)", opacity: dim ? 0.6 : 1 }}>
                <td className="tnum" style={{ ...td, textAlign: "left" }}>{formatDay(e.occurredAt)}</td>
                <td style={{ ...td, textAlign: "left" }}>
                  {e.userId ? (
                    <Link to="/admin/users/$userId" params={{ userId: e.userId }} style={{ color: "var(--sky-300)", textDecoration: "none" }}>
                      {e.userName ?? e.userEmail ?? "Unknown"}
                    </Link>
                  ) : (
                    <span style={{ color: "var(--text-muted)" }}>Account removed</span>
                  )}
                </td>
                <td style={{ ...td, textAlign: "left" }}>{describe(e)}</td>
                <td style={{ ...td, textAlign: "left" }}>{sourceLabel(e.provider)}</td>
                <td className="tnum" style={{ ...td, textDecoration: dim ? "line-through" : "none" }}>
                  {formatPence(e.amountPence)}
                  {e.listPrice && <span style={{ color: "var(--text-muted)", marginLeft: 4 }}>list</span>}
                </td>
                <td style={{ ...td, textAlign: "left", whiteSpace: "normal" }}><StatusCell e={e} /></td>
                <td style={td}>
                  {canToggle && (
                    <Button size="sm" variant="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate(e)}>
                      {e.counted ? "Not real money" : "Count it"}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function Tiles({ r }: { r: RevenueReport }) {
  const s = r.subscriptions;
  return (
    <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      <AtlasPanel>
        <StatBlock
          size="sm"
          label="Total revenue"
          gold
          value={money(r.totals.allTimePence)}
          sub={`${formatPence(r.totals.thisMonthPence)} this month · about ${formatPence(r.totals.afterFeesPence)} after fees`}
        />
      </AtlasPanel>
      <AtlasPanel>
        <StatBlock
          size="sm"
          label="Subscriptions per month"
          value={money(s.monthlyValuePence)}
          sub={s.paying.length === 0 ? "nobody paying yet" : `${plural(s.yearlyCount, "yearly", "yearly")} · ${plural(s.monthlyCount, "monthly", "monthly")} paying`}
        />
      </AtlasPanel>
      <AtlasPanel>
        <StatBlock
          size="sm"
          label="Full history one-offs"
          value={money(r.fullHistory.totalPence)}
          sub={`${r.fullHistory.soldCount} sold${r.fullHistory.refundedCount > 0 ? ` · ${r.fullHistory.refundedCount} refunded` : ""}`}
        />
      </AtlasPanel>
      <AtlasPanel>
        <StatBlock
          size="sm"
          label="Cancelled or ended"
          value={s.endedLast30Days.toLocaleString()}
          sub={`last 30 days · ${s.cancellingCount} set to cancel`}
        />
      </AtlasPanel>
    </div>
  );
}

export default function AdminRevenuePage() {
  const { user } = useAuth();
  const [months, setMonths] = React.useState<6 | 12 | 24>(12);
  const enabled = !!user?.isAdmin;

  const revenueQuery = useQuery({
    queryKey: ["admin/subscriptions/revenue", months],
    queryFn: () => fetchRevenueReport(months),
    enabled,
  });
  const dunningQuery = useQuery({
    queryKey: ["admin/subscriptions/dunning"],
    queryFn: fetchSubscriptionDunning,
    enabled,
  });
  const conflictsQuery = useQuery({
    queryKey: ["admin/subscriptions/conflicts"],
    queryFn: fetchSubscriptionConflicts,
    enabled,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const r = revenueQuery.data?.revenue;
  const th: React.CSSProperties = { fontSize: 9, textAlign: "right", padding: "0 8px 6px", whiteSpace: "nowrap" };
  const td: React.CSSProperties = { padding: "6px 8px", textAlign: "right", color: "var(--text-primary)", whiteSpace: "nowrap" };
  const activeMonths = r ? [...r.months].reverse().filter((m) => m.netPence !== 0 || m.newSubscriptions + m.renewals + m.fullHistorySales + m.ended > 0 || m.refundsPence > 0) : [];

  return (
    <AdminShell
      title="Revenue"
      action={
        <Segmented
          value={String(months)}
          options={[{ value: "6", label: "6 mo" }, { value: "12", label: "12 mo" }, { value: "24", label: "24 mo" }]}
          onChange={(v) => setMonths(v === "6" ? 6 : v === "24" ? 24 : 12)}
        />
      }
    >
      {revenueQuery.isLoading || !r ? (
        <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {Array.from({ length: 4 }, (_, i) => <Skel key={i} height={72} />)}
        </div>
      ) : (
        <>
          <Tiles r={r} />
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "-8px 0 0", lineHeight: 1.5 }}>
            Totals are after refunds. App Store and Google Play sales count at list price, before the store's cut.
            "After fees" is an estimate: 15% for the stores, 1.5% + 20p for card payments. Test purchases, admin accounts
            and anything marked not real money are listed under Every sale but never counted. Gifted Pro is never revenue.
          </p>

          <AtlasPanel title="Sales by month">
            <MonthBars months={r.months} />
          </AtlasPanel>

          <AtlasPanel title="Monthly breakdown">
            {activeMonths.length === 0 ? (
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>Nothing in this window yet.</p>
            ) : (
              <div className="r-scroll-x" style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 640, width: "100%" }}>
                  <thead>
                    <tr>
                      <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Month</th>
                      <th className="eyebrow" style={th}>New subscriptions</th>
                      <th className="eyebrow" style={th}>Renewals</th>
                      <th className="eyebrow" style={th}>Full history sold</th>
                      <th className="eyebrow" style={th}>Cancelled or ended</th>
                      <th className="eyebrow" style={th}>Refunds</th>
                      <th className="eyebrow" style={th}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeMonths.map((m) => (
                      <tr key={m.month} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                        <td className="tnum" style={{ ...td, textAlign: "left" }}>{formatMonth(m.month)}</td>
                        <td className="tnum" style={td}>{m.newSubscriptions}</td>
                        <td className="tnum" style={td}>{m.renewals}</td>
                        <td className="tnum" style={td}>{m.fullHistorySales}</td>
                        <td className="tnum" style={td}>{m.ended}</td>
                        <td className="tnum" style={td}>{m.refundsPence > 0 ? `-${formatPence(m.refundsPence)}` : formatPence(0)}</td>
                        <td className="tnum" style={{ ...td, fontWeight: 600 }}>{formatPence(m.netPence)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontSize: 11, color: "var(--text-muted)", margin: "8px 0 0" }}>Months with no activity are hidden.</p>
              </div>
            )}
          </AtlasPanel>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
            <AtlasPanel title={`Paying subscribers · ${r.subscriptions.paying.length}`}>
              {r.subscriptions.paying.length === 0 ? (
                <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>Nobody is paying for Pro right now.</p>
              ) : (
                <div className="r-scroll-x" style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: 420 }}>
                    <thead>
                      <tr>
                        <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Who</th>
                        <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Plan</th>
                        <th className="eyebrow" style={th}>Per month</th>
                        <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Next</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.subscriptions.paying.map((p) => (
                        <tr key={p.userId} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                          <td style={{ ...td, textAlign: "left" }}>
                            <Link to="/admin/users/$userId" params={{ userId: p.userId }} style={{ color: "var(--sky-300)", textDecoration: "none" }}>
                              {p.userName ?? p.userEmail ?? "Unknown"}
                            </Link>
                          </td>
                          <td style={{ ...td, textAlign: "left" }}>{p.product === "yearly" ? "Yearly" : "Monthly"}, {sourceLabel(p.provider)}</td>
                          <td className="tnum" style={td}>{formatPence(p.monthlyPence)}</td>
                          <td style={{ ...td, textAlign: "left" }}>
                            {p.cancelling ? `Ends ${formatDay(p.renewsOrEndsAt)}` : `Renews ${formatDay(p.renewsOrEndsAt)}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </AtlasPanel>

            <AtlasPanel title="Gifted Pro (not revenue)">
              <StatBlock
                size="sm"
                label="Gifted Pro"
                value={r.gifted.proTotal.toLocaleString()}
                sub={r.gifted.proBySource.map((g) => `${GIFT_LABEL[g.source] ?? sourceLabel(g.source)} ${g.count}`).join(" · ") || "none"}
              />
              <div style={{ marginTop: 16 }}>
                <StatBlock size="sm" label="Full history given free" value={r.gifted.fullHistoryCount.toLocaleString()} sub="admin and partner grants" />
              </div>
            </AtlasPanel>
          </div>

          <AtlasPanel title="Every sale">
            <SalesTable entries={r.entries} th={th} td={td} />
            <p style={{ fontSize: 11, color: "var(--text-muted)", margin: "8px 0 0", lineHeight: 1.5 }}>
              "Not real money" takes a sale out of every figure (and its refund with it). It never changes anyone's plan.
              Yearly Pro also unlocks Full history; that unlock is part of the subscription, not a separate sale.
            </p>
          </AtlasPanel>
        </>
      )}

      {/* Dunning */}
      <AtlasPanel title="Dunning">
        {dunningQuery.isLoading ? (
          <Skel height={40} />
        ) : !dunningQuery.data || dunningQuery.data.dunning.length === 0 ? (
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>Nobody is past due or in a store grace period.</p>
        ) : (
          <div className="r-scroll-x" style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: 560 }}>
              <thead>
                <tr>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>User</th>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Source</th>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>State</th>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Since</th>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Emails</th>
                </tr>
              </thead>
              <tbody>
                {dunningQuery.data.dunning.map((d) => (
                  <tr key={d.userId} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td style={{ ...td, textAlign: "left" }}>
                      <Link to="/admin/users" search={{ q: d.email }} style={{ color: "var(--sky-300)", textDecoration: "none" }}>
                        {d.name ?? d.email}
                      </Link>
                    </td>
                    <td style={{ ...td, textAlign: "left" }}>{sourceLabel(d.planSource)}</td>
                    <td style={{ ...td, textAlign: "left" }}><Badge variant="danger">{d.state === "past_due" ? "Past due" : "Grace"}</Badge></td>
                    <td className="tnum" style={{ ...td, textAlign: "left" }}>{formatDay(d.since)}</td>
                    <td style={{ ...td, textAlign: "left", color: "var(--text-secondary)", whiteSpace: "normal" }}>
                      {d.emails.length === 0 ? "none" : d.emails.map((e) => `${e.kind} ${e.status} ${formatDay(e.sentAt)}`).join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AtlasPanel>

      {/* Conflicts */}
      <AtlasPanel title="Cross-store conflicts">
        {conflictsQuery.isLoading ? (
          <Skel height={40} />
        ) : !conflictsQuery.data || conflictsQuery.data.conflicts.length === 0 ? (
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No accounts with more than one store id or a plan source the ids do not back.</p>
        ) : (
          <div className="r-scroll-x" style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%", minWidth: 520 }}>
              <thead>
                <tr>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>User</th>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Plan</th>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Source</th>
                  <th className="eyebrow" style={{ ...th, textAlign: "left" }}>Store ids</th>
                </tr>
              </thead>
              <tbody>
                {conflictsQuery.data.conflicts.map((r) => (
                  <tr key={r.userId} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td style={{ ...td, textAlign: "left" }}>
                      <Link to="/admin/users" search={{ q: r.email }} style={{ color: "var(--sky-300)", textDecoration: "none" }}>
                        {r.name ?? r.email}
                      </Link>
                    </td>
                    <td style={{ ...td, textAlign: "left" }}>{r.plan}{r.planExpiresAt ? ` to ${formatDay(r.planExpiresAt)}` : ""}</td>
                    <td style={{ ...td, textAlign: "left" }}>{sourceLabel(r.planSource)}</td>
                    <td style={{ ...td, textAlign: "left" }}>{r.stores.map(sourceLabel).join(", ") || "none"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 11, color: "var(--text-muted)", margin: "8px 0 0" }}>
              Resolve from the user drawer: clear a stale store id once a fresh entitlement fetch confirms it is inactive.
            </p>
          </div>
        )}
      </AtlasPanel>
    </AdminShell>
  );
}
